import {
  ProConfigProvider,
  ProTable,
  enUSIntl,
  roROIntl,
  ruRUIntl
} from '@ant-design/pro-components'
import type { ProColumns, ProTableProps } from '@ant-design/pro-components'
import { Flex, Space, theme } from 'antd'
import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { memo, useDeferredValue, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { TABLE_COLUMNS_COUNT } from 'src/constants'
import {
  usePaginationConfig,
  useQueryUpdate,
  useSessionContext
} from 'src/hooks'
import type { PaginationTypes } from 'src/hooks'
import {
  countVisibleColumns,
  enhanceColumns,
  getSortStateFromOrdering,
  resolveLanguageKey
} from 'src/utils'

import { SearchInput } from '../Inputs'

import { ColumnHeaderProvider } from './columnHeader'
import { ColumnSettings } from './ColumnSettings/ColumnSettings'
import type { ColumnSettingsConfig } from './ColumnSettings/types'
import {
  useContainerWidth,
  useDeferredRowSelection,
  useStickyHeader,
  useTableColumnsState
} from './hooks'
import { useStyles } from './styles'
import { columnPinsOf } from './treadmillMath'
import { useTreadmillStyles } from './treadmillStyles'
import type { TableVariant } from './types'
import { useTableVirtualization } from './useTableVirtualization'

const explicitProTableIntlByLanguage = {
  en: enUSIntl,
  ro: roROIntl,
  ru: ruRUIntl
}

export type TableContainerProps<T extends Record<string, unknown>> = {
  data?: PaginationTypes<T[]>
  columns?: ColumnsType<T>
  isLoading?: boolean
  leftChildren?: ReactNode
  rightChildren?: ReactNode
  cardTitle?: ReactNode
  cardExtra?: ReactNode
  showSearch?: boolean
  searchPlaceholder?: string
  isHoverAlternative?: boolean
  children?: ReactNode
  columnsReorderable?: boolean
  columnsState?: ProTableProps<T, Record<string, unknown>>['columnsState']
  options?: ProTableProps<T, Record<string, unknown>>['options']
  tableIdentifier?: string
  variant?: TableVariant
  columnSettings?: ColumnSettingsConfig
} & TableProps<T>

const NO_ROWS: never[] = []

const ProTableContainerComponent = <T extends Record<string, unknown>>({
  data,
  columns = [],
  isLoading = false,
  leftChildren,
  rightChildren,
  cardTitle,
  cardExtra,
  showSearch = true,
  searchPlaceholder,
  children,
  columnsReorderable = true,
  columnsState,
  options,
  tableIdentifier,
  bordered = false,
  size = 'small',
  variant,
  columnSettings,
  ...tableProps
}: TableContainerProps<T>) => {
  const { styles } = useStyles({
    onRow: !!tableProps?.onRow,
    variant
  })
  const { styles: treadmillStyles } = useTreadmillStyles()
  const { token } = theme.useToken()
  const rootRef = useRef<HTMLDivElement>(null)
  const containerWidth = useContainerWidth(rootRef)
  const { t, i18n } = useTranslation()
  const { params } = useQueryUpdate()
  const { user, activeWorkspaceId } = useSessionContext()
  const proTableIntl =
    explicitProTableIntlByLanguage[resolveLanguageKey(i18n.language)]

  const hasLeftTitle = showSearch || Boolean(leftChildren)

  const titleContent = hasLeftTitle ? (
    <Flex align="center" wrap gap={8}>
      {showSearch && (
        <Space>
          <SearchInput
            size={size}
            placeholder={searchPlaceholder ?? t('search')}
          />
        </Space>
      )}

      {leftChildren}
    </Flex>
  ) : undefined

  const sortStateFromQuery = useMemo(
    () => getSortStateFromOrdering(params.ordering),
    [params.ordering]
  )

  const enhancedColumns = useMemo(
    () => enhanceColumns(columns, sortStateFromQuery),
    [columns, sortStateFromQuery]
  )

  const defaultPagination = usePaginationConfig(data)
  const pagination = tableProps?.pagination ?? defaultPagination
  const styledPagination =
    pagination === false
      ? false
      : {
          ...pagination,
          className: [pagination.className, styles.pagination]
            .filter(Boolean)
            .join(' ')
        }

  const {
    dataSource,
    pagination: _pagination,
    rowSelection,
    rowKey,
    rowClassName,
    scroll,
    virtual,
    tableLayout,
    components,
    sticky,
    expandable,
    onRow,
    onChange,
    onScroll,
    className,
    loading,
    ...restTableProps
  } = tableProps

  const tableClassName = [styles.table, className].filter(Boolean).join(' ')
  const isFirstPaintPending = useDeferredValue(false, true)
  const resolvedLoading = (loading ?? isLoading) || isFirstPaintPending
  const resolvedDataSource = isFirstPaintPending
    ? NO_ROWS
    : (dataSource ?? data?.results ?? [])
  const resolvedRowKey = rowKey ?? 'id'

  const columnCount = useMemo(() => countVisibleColumns(columns), [columns])
  const definedPins = useMemo(() => columnPinsOf(columns), [columns])

  const deferredRowSelection = useDeferredRowSelection(rowSelection)

  const persistenceScope = useMemo(
    () => ({
      organizationId: activeWorkspaceId,
      userId: user?.user?.id
    }),
    [activeWorkspaceId, user]
  )

  const {
    columnsMap,
    isApplyingColumns,
    columnWidths,
    setColumnWidth,
    resolvedColumnsState,
    changeColumnsMap,
    reorderColumns,
    resetColumnsLayout
  } = useTableColumnsState<T>({
    columnsState,
    tableIdentifier,
    columnCount,
    scope: persistenceScope,
    persistence: columnSettings?.persistence,
    defaultHiddenKeys: columnSettings?.defaultHiddenKeys,
    definedPins
  })

  const { emptyText } = tableProps.locale ?? {}
  const resolvedEmptyText =
    typeof emptyText === 'function' ? emptyText() : emptyText

  const virtualization = useTableVirtualization({
    columns: enhancedColumns,
    columnCount,
    containerWidth,
    columnsMap,
    columnWidths,
    dataSource: resolvedDataSource,
    pageSize: pagination === false ? undefined : pagination.pageSize,
    virtual,
    scroll,
    tableLayout,
    components,
    sticky: sticky ?? true,
    rowKey: resolvedRowKey,
    rowSelection: deferredRowSelection,
    expandable,
    rowClassName,
    onRow,
    emptyText: resolvedEmptyText
  })

  const defaultOptions: ProTableProps<T, Record<string, unknown>>['options'] = {
    density: false,
    reload: false,
    fullScreen: false,
    setting: false
  }

  const resolvedOptions =
    options === undefined
      ? defaultOptions
      : options === false
        ? false
        : { ...defaultOptions, ...options }

  const columnSettingsControl = columnCount >= TABLE_COLUMNS_COUNT &&
    options !== false && (
      <ColumnSettings
        key="column-settings"
        columns={enhancedColumns}
        columnsMap={columnsMap}
        icons={columnSettings?.icons}
        hints={columnSettings?.hints}
        lockedKeys={columnSettings?.lockedKeys}
        hiddenGroups={columnSettings?.hiddenGroups}
        actions={columnSettings?.actions}
        customization={columnSettings?.customization}
        size={size}
        onChange={changeColumnsMap}
        onReset={resetColumnsLayout}
      />
    )

  const headerPins = useMemo(
    () => columnPinsOf(virtualization.columns),
    [virtualization.columns]
  )

  const stickyHeaderOffset = useStickyHeader(
    rootRef,
    virtualization.virtual
      ? 'virtual'
      : virtualization.sticky === true
        ? 'sticky'
        : 'none'
  )

  const hasToolbarActions = Boolean(rightChildren || columnSettingsControl)

  const resolvedTableClassName = [
    tableClassName,
    treadmillStyles.uniformRows,
    treadmillStyles.resizableHeader,
    treadmillStyles.edgeShadows,
    virtualization.virtual && treadmillStyles.virtualHeader
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Flex vertical ref={rootRef}>
      <ColumnHeaderProvider
        rootRef={rootRef}
        columnKeys={virtualization.columnKeys}
        containerWidth={containerWidth}
        totalWidth={
          typeof virtualization.scroll?.x === 'number'
            ? virtualization.scroll.x
            : 0
        }
        onResize={setColumnWidth}
        onReorder={reorderColumns}
        pinOffsets={virtualization.headerPins}
        columnPins={headerPins}
        reorderable={columnsReorderable}
      >
        <ProConfigProvider intl={proTableIntl}>
          <ProTable<T, Record<string, unknown>>
            headerTitle={titleContent}
            toolBarRender={
              hasToolbarActions
                ? () => [
                    <Flex key="actions" align="center" gap={token.marginXXS}>
                      {rightChildren}
                      {columnSettingsControl}
                    </Flex>
                  ]
                : undefined
            }
            toolbar={{ className: styles.toolbar }}
            tableAlertRender={false}
            search={false}
            options={resolvedOptions}
            columnsState={resolvedColumnsState}
            rowKey={resolvedRowKey}
            loading={isApplyingColumns || resolvedLoading}
            dataSource={resolvedDataSource}
            columns={
              virtualization.columns as unknown as ProColumns<T, 'text'>[]
            }
            scroll={virtualization.scroll}
            tableLayout={virtualization.tableLayout}
            components={virtualization.components}
            sticky={
              virtualization.sticky === true
                ? { offsetHeader: stickyHeaderOffset }
                : virtualization.sticky
            }
            expandable={virtualization.expandable}
            pagination={styledPagination}
            rowSelection={virtualization.rowSelection}
            onChange={onChange}
            className={resolvedTableClassName}
            rowClassName={rowClassName}
            onRow={onRow}
            onScroll={onScroll}
            cardProps={{
              title: cardTitle,
              extra: cardExtra,
              headerBordered: Boolean(cardTitle || cardExtra),
              styles: { body: { padding: 0 } }
            }}
            styles={{ header: { wrapper: { padding: 0 } } }}
            bordered={bordered}
            size={size}
            {...restTableProps}
          />
        </ProConfigProvider>
      </ColumnHeaderProvider>
      {children}
    </Flex>
  )
}

export const ProTableContainer = memo(
  ProTableContainerComponent
) as typeof ProTableContainerComponent
